import React, { useState, Suspense } from "react";
import { useInView } from "react-intersection-observer";

/* components */
import ExpandableDefinition from "./ExpandableDefinition";
import DefinitionCard from "./DefinitionCard";
import AiDeerDiagram from "./AiDeerDiagram";
import FoodImpactDiagram from "./FoodImpactDiagram.jsx";
import FLCSunburstDiagram from "./FLCSunburstDiagram.jsx";

/* styles */
import "/styles/modern-normalize.css";
import "/styles/global.css";
import "/styles/components/cover.css";
import "/styles/components/header.css";
import "/styles/components/main.css";
import "/styles/components/expandcontent.css";
import "/styles/components/paintings.css";
import "/styles/utility.css";

/* import lazy models */
const LazyMochidonut = React.lazy(() =>
  import("../models/Mochidonut").then((mod) => ({
    default: mod.Mochidonut,
  }))
);

const LazyDalgonacoffee = React.lazy(() =>
  import("../models/Dalgonacoffee").then((mod) => ({
    default: mod.Dalgonacoffee,
  }))
);
const LazyDanishgreentea = React.lazy(() =>
  import("../models/Danishgreentea").then((mod) => ({
    default: mod.Danishgreentea,
  }))
);
const LazyKesari = React.lazy(() =>
  import("../models/Kesari").then((mod) => ({
    default: mod.Kesari,
  }))
);
const LazyMushroomhandpie = React.lazy(() =>
  import("../models/Mushroomhandpie").then((mod) => ({
    default: mod.Mushroomhandpie,
  }))
);
const LazyFruitteatrifle = React.lazy(() =>
  import("../models/Fruitteatrifle").then((mod) => ({
    default: mod.Fruitteatrifle,
  }))
);

/* card function */
export default function Main() {
  const [activeCard, setActiveCard] = useState({
    word: null,
    paragraphId: null,
  });
  const [hoverCard, setHoverCard] = useState({ word: null, paragraphId: null });
  // Click: Toggle the active word (click to expand/collapse)
  const handleExpand = (word, paragraphId) => {
    setHoverCard({ word: null, paragraphId: null }); // clear hover
    setActiveCard({ word, paragraphId }); // always set new card
  };
  // Hover: Show definition temporarily
  const handleHover = (word, paragraphId) => {
    setHoverCard(
      word ? { word, paragraphId } : { word: null, paragraphId: null }
    );
  };

  // Determine which word should display (hover > click)
  const displayedCard = hoverCard.word ? hoverCard : activeCard;

  /* card information */
  const cardData = {
    /* prologue 2039 */
    p1: {
      "WateryTrees declared a food shortage emergency": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#waterytrees",
      },
      "the wetlands are largely restored": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#wetlands",
      },
      "circular economy": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#circular-economy",
      },
    },
    p2: {
      "food desert turned into a food oasis": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    /* introduction */
    p3: {
      "Lakeshore District": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
      WateryTrees: {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    /* disclaimer */
    p4: {
      "speculative fiction": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    /* Rosa 2020-2024 */
    p5: {
      "community rain gardens": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p6: {
      "white paint on the roofs and streets": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p7: {
      "Food Impact Diagram Source": {
        /* image: "./images/Stone Orange.webp", */
        text: "Michael Clark et al (2022). Estimating the environmental impacts of 57,000 food products. PNAS. – processed by Our World in Data",
        learnMoreLink:
          "https://ourworldindata.org/explorers/food-footprints?showSelectionOnlyInTable=1&pickerSort=desc&pickerMetric=ghg_kg&Commodity+or+Specific+Food+Product=Specific+food+products&Environmental+Impact=Carbon+footprint&Kilogram+%2F+Protein+%2F+Calories=Per+kilogram&By+stage+of+supply+chain=false",
        referenceLink: "/references#food-oasis",
      },
    },
    p8: {
      "fancy living pergolas": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p9: {
      "after rent, we had to choose between hydro or food": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    /* Orson 2025-2029 */
    p10: {
      "floods we’re predicted to get in 50 years": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p11: {
      "Terminal flooded": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p12: {
      "vertical gardens": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p13: {
      "dataviz who's more likely to require the food bank": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p14: {
      "EcoSafe program": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p15: {
      "social enterprise": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p16: {
      "niche-intelligence": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    /* Indra 2030-2034 */
    p17: {
      "the Sponge's many rivers and creeks": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p18: {
      "eastern suburbs near the Greenbelt": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
      "micro urban farms": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p19: {
      biomimicry: {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p20: {
      "Food life cycle information availability": {
        /*  image: "./images/Stone Orange.webp", */
        text: "source and method of data collection TBD. Definitions for each part of the food life cycle are sourced from ChatGPT and will be updated soon",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p21: {
      "how much energy and water that deer likely consumed": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p22: {
      "Grow Market": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p23: {
      "repurposed into housing and vertical gardens": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    /* Janey 2035-2039 */
    p24: {
      stewardship: {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p25: {
      "the Elders": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p26: {
      "Food Systems Education": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p27: {
      "Visitors are actively encouraged to forage here": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    p28: {
      "Dataviz qualitative viz on Sponge resource and services": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
    /* Afterword */
    p29: {
      "Pumpkinseed, Walleye, Salmon, Pike": {
        image: "./images/Stone Orange.webp",
        text: "placeholdercard",
        learnMoreLink: "https://about-me-saachi-sadchas-projects.vercel.app/",
        referenceLink: "/references#food-oasis",
      },
    },
  };

  /* for lazy loaded models */
  const { ref: ref1, inView: inView1 } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });
  const { ref: ref2, inView: inView2 } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });
  const { ref: ref3, inView: inView3 } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });
  const { ref: ref4, inView: inView4 } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });
  const { ref: ref5, inView: inView5 } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });
  const { ref: ref6, inView: inView6 } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });

  document.addEventListener("contextmenu", (e) => {
    if (e.target.closest(".lgpainting")) {
      e.preventDefault();
    }
  });

  /* Short Story */
  return (
    <main>
      {/* section: title */}
      <section id="cover" className="cover">
        <h1 className="cover__title"> WateryTrees </h1>
        <h2 className="cover__subtitle"> The Sponge, 2039 </h2>
        <span className="cover__image-temp">
          Placeholder
          <br />
          <br /> type: painting, fill
          <br /> story: Spongy Cake Pastry House in the wetlands in the morning
          <br /> feeling: calm but full of life
        </span>
        <h2 className="cover__author">Story and art by Saachi Sadcha</h2>
        <div className="scroll-indicator">
          <p className="scroll-label">Scroll to Keep Reading</p>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="scroll-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m4.5 5.25 7.5 7.5 7.5-7.5m-15 6 7.5 7.5 7.5-7.5"
            />
          </svg>
        </div>
      </section>

      {/* section: Prologue2039 */}
      <section id="prologue" className="story container section">
        <h3 className="chapter__subtitle">Prologue</h3>
        <h2 className="chapter__title-year"> 2039</h2>
        <p className="chapter__text">
          On a certain summer morning in 2039, I stepped onto the deck of a
          moss-encrusted bakery on the outer edge of the Sponge.
        </p>

        <p className="chapter__text">
          The bakery had three solid walls, a floor, and a sloping roof; all
          made of timber and repurposed steel. When I opened the door, a soft
          bell rang and invisible molecules of powdered sugar and frying dough
          floated past my nose. I spotted the mismatching canisters of spices
          and sugar lining the shelves taller than any human could reach up to,
          and the flour sacks heaped pleasingly on the floor. At the counter, a
          vendor with a pleasant and round face silently pointed out the special
          “items of the day” to the other early-morning customers. I joined the
          line.
        </p>

        {/* mochidonut insert */}
        <div className="canvas-container" ref={ref1}>
          {inView1 && (
            <Suspense fallback={<p>Loading donut...</p>}>
              <LazyMochidonut />
            </Suspense>
          )}
        </div>
        <p className="chapter__text">
          “Maple walnut, and an elderberry, please,” I requested when it was my
          turn, and pointed directly at the colourful mochi donuts - eight small
          spheres of chewy, fried pastries arranged in a ring and topped with
          sweet glazes and edible decorations.
        </p>
        <p className="chapter__text">
          The pastries were slipped onto a plate and the amount owed flickered
          across my retina, courtesy of a chip stuck to my temple. I reflexively
          tapped a white square sitting on a counter with the tip of my finger
          to pay.
        </p>
        <p className="chapter__text">
          The fourth wall was mostly glass with a long wooden bar and
          highstools. Like the seating in the middle of the bakery, most of the
          stools were free. Before I could tuck myself into one, I took a moment
          to appreciate the quiet, expansive view of the lake beyond the Sponge.
          Fishing boats littered the horizon line, near tree and cottage filled
          islands.
        </p>
        <p className="chapter__text">
          The solar film on the window dimmed the morning sun a bit, but could
          not quite stop the assault of twinkling light on the wide lake. Nor
          could it stem the gasp of rising humidity when the door opened.
        </p>
        <p className="chapter__text">
          I wiped my sweaty palms on my shorts and tore a glazed sphere from the
          maple walnut. As I did, a monstrous deer entered the water. The
          creature barely disturbed the water with its thin, ghostly legs as it
          ventured closer to the middle. A few small sailboats listed towards
          the deer, lazily and without ill-intent. Not that they could've hurt
          the creature even if they wanted to.
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: painting, fill
          <br /> story: deer on the water, with boats arriving to meet it, with
          sponge and city in the background
          <br /> feeling: peaceful awe
        </span>
        <p className="chapter__text">
          Five years ago, this deer used to be part of the artificially
          intelligent labour force. Then, following an update, it shed its
          soft-body, biomemetic form ingrained with protocols and{" "}
          <em>evolved</em>. The assumption was that the deer and all its kind
          watched us from afar but never engaged. There was nothing we could
          base this assumption on, other than the overwhelming sense that we
          were still the center of their world. Clung to this belief as we clung
          to the tools AI helped us build.
        </p>
        <p className="chapter__text">
          With a quick, almost compulsive wink, I took a photo which made my
          temple pulse. The notification was a tad too weak, I noted absently
          for the fifth time this month. I retrieved a battered notebook from my
          backpack, and after a pause, watched the image of the AI deer bleed
          onto the pages. With my fingers and a thin stylus, I resized the image
          and blurred the edges a bit, even jotted down some notes with arrows.
          After rubbing my brow a bit, I added an experimental note:
        </p>
        <p className="chapter__text">
          “White-tailed deer, both real and artificial, roam the ephemeral ponds
          and wetlands fat with nutrients where there was once only concrete and
          sailboats. In late autumn and winter, hunting season will open up and
          the shacks and restaurants in the Sponge will serve venison shot right
          on their watery doorstep.”{" "}
        </p>
        <div>
          <AiDeerDiagram />
        </div>

        <p className="chapter__text">
          Unfortunately, It was hard to imagine eating a cozy bowl of
          ethically-caught and butchered venison stew in this sweltering heat.
          The sweat dripped from the tip of my nose to stain my notebook with
          tiny splashes.
        </p>
        <p className="chapter__text">
          I tapped my pen against the surface of my notebook and gazed up at the
          canisters of coffee stacked high on the shelves. It was likely that
          the ground-up brown stuff was made of only 10% coffee beans. The rest
          was likely chicory, my augmented reality chip supplied, with a flicker
          that seemed like hesitance. Maybe even hyacinth? I snapped the maple
          leaf-shaped candy between my teeth and stuffed the first three spheres
          of the maple mochi donut into my mouth.
        </p>
        <p className="chapter__text">
          When was the last time I'd had a cup of 100% coffee? When had anyone?
          I flipped through my notebook till I came upon my editor's note.
        </p>
        {/* expandable definition */}
        <p className="chapter__text">
          ‘20 years ago,{" "}
          <ExpandableDefinition
            word="WateryTrees declared a food shortage emergency"
            paragraphId="p1"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          . The response was a 100-year plan to address the shortage as well as
          the ongoing impacts from a worsening climate. This resulted in large
          changes to our urban environment. Nowhere more so than in the
          Lakeshore District. Vertical gardens form the core of skyscrapers,{" "}
          {/* expandable definition */}
          <ExpandableDefinition
            word="the wetlands are largely restored"
            paragraphId="p1"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          , a giant highway of produce reigns as lord over the lands, and a{" "}
          {/* expandable definition */}
          <ExpandableDefinition
            word="circular economy"
            paragraphId="p1"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          has outlasted the sudden AI boom that built it.’
        </p>

        {/* definition cards */}
        <DefinitionCard
          paragraphId="p1"
          activeCard={displayedCard}
          cardData={cardData}
        />

        <p className="chapter__text">
          ‘So what do our readers think? Are they fed? Are they happy? Has the{" "}
          <ExpandableDefinition
            word="food desert turned into a food oasis"
            paragraphId="p2"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          . In this edition…’
        </p>

        {/* definition cards */}
        <DefinitionCard
          paragraphId="p2"
          activeCard={displayedCard}
          cardData={cardData}
        />

        <p className="chapter__text">
          ‘That last bit might be a bit much,’ I muttered. I chewed on the last
          of my donuts and waited for my first interviewee to join me.
        </p>
      </section>

      {/* section: Editor’s Note + Introduction */}
      <section id="intro" className="story container section">
        <h3 className="chapter__subtitle">Editor’s Note</h3>
        <h2 className="chapter__title-year">Introduction</h2>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: animated png, half fill
          <br /> animation of natural coastline becoming urban
          <br />
        </span>
        <p className="chapter__text">
          The{" "}
          <ExpandableDefinition
            word="Lakeshore District"
            paragraphId="p3"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          is located in the oldest part of{" "}
          <ExpandableDefinition
            word="WateryTrees"
            paragraphId="p3"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          , a Canadian Great Lakes city. WateryTree’s natural, ravine-scarred
          landscape and extensive coastline changed greatly following centuries
          of colonization and intense urbanization. Now, most of the Lakeshore
          District extends well past the natural coastline of Lake Ontario.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p3"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          As a financial and tourist hub, this district has historically enjoyed
          higher-than-average income and excellent quality of life. Despite
          rising housing costs and stagnant incomes, the residents had less
          cause to be concerned about affording basic needs. This all changed in
          the early 2000s.
        </p>
        <p className="chapter__text">
          Food banks all over WateryTrees reported a rise in users, and a food
          emergency was declared. At the same time, increased floods and heat
          waves put further pressure on the city's infrastructure and budgets.
          People who’d never known poverty were forced into bankruptcy as they
          spent more on insurance and health bills. Eventually, even wealthy
          pockets, including the Lakeshore District, sounded alarms as
          cost-of-living rose and food security fell.
        </p>
        <p className="chapter__text">
          Today, the satellite view of Lakeshore District looks more like a
          bloated version of its former pre-concrete self. Lush with wild
          vegetation and crisscrossed with thin rivers, the land is permeable
          and fertile. Smart pathways extend like delicate roots into a marshy
          wetland, affectionally known as the Sponge, that fringes around a
          great lake.
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: large painting, fill
          <br /> story: morning drone view of Lakeshore District and Lake
          <br /> feeling: bustle, excitement
          <br />
        </span>
        <p className="chapter__text">
          But look closer, and it's clear this ecosystem was structurally
          engineered by an invasive, hopeful species that is only trying its
          best to survive in an increasingly unstable world. Food markets, urban
          gardens, and circular agriculture dominate industries still reeling
          from the sudden arrival and departure of Artificial Intelligence (AI)
          and soft-body robotics. The memories of destroyed buildings are
          visible in repurposed, rusting material. Food is plentiful and
          changed.
        </p>
        <p className="chapter__text">
          So what do our readers think? Are they fed? Are they happy? Has the
          food desert turned into a food oasis. In this edition of City Cuisine,
          dear readers, we give you four stories of what life was like for the
          last 20 years in the Lakeshore District, WateryTrees' first food
          oasis.
        </p>
        {/* editors pic and bio */}
      </section>

      {/* section: Disclaimer */}
      <section id="disclaimer" className="story container section">
        <h2 className="chapter__title">Disclaimer</h2>
        <p className="chapter__text">
          Copyright © 2025 Saachi Sadcha - All Rights Reserved.
        </p>
        <p className="chapter__text">
          This site is a work of{" "}
          <ExpandableDefinition
            word="speculative fiction"
            paragraphId="p4"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          and is currently under construction. It's heavily based on real-world
          data and solutions which do not belong to me, and are referenced via
          the expanding cards. While I did my best to interpret data available,
          I am not an expert in urban environments or food insecurity. All
          mistakes are my own. Story, images, and models belong to Saachi
          Sadchatheeswaran. Do not copy, download, or save elsewhere.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p4"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          Further information and credits can be found in References.
        </p>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: small painting, graytone
          <br /> story: Saachi reading at desk which is littered with models
          from story
          <br /> feeling: cozy
          <br />
        </span>
      </section>

      {/* section: Tutorial */}
      <section id="tutorial" className="story container section">
        <h2 className="chapter__title">Tutorial</h2>
        <p className="chapter__text">TBD if necessary</p>
      </section>

      {/* section: 2020-2024 */}
      <section id="2020-2024" className="story container section">
        <h3 className="chapter__subtitle">Rosa</h3>
        <h2 className="chapter__title-year">2020-2024</h2>
        <p className="chapter__text">
          "Here, have an oat latte on the house.”{" "}
        </p>
        {/* whipped coffee insert */}
        <div className="canvas-container" ref={ref2}>
          {inView2 && (
            <Suspense fallback={<p>Loading coffee...</p>}>
              <LazyDalgonacoffee />
            </Suspense>
          )}
        </div>
        <p className="chapter__text">
          Rosa Santos, co-owner of the Spongy Cake Pastry House placed a glass
          coated in stripes of what could only be lab-grown chocolate in front
          of me. I thanked her profusely and tried to hint that I could just
          bill the publishing house, but she refused any payment with a laugh,
          so I gushed a bit about the mochi donuts instead.
        </p>
        <p className="chapter__text">
          The cashier, who I found out later was her son, Antonio, followed with
          a platter topped with a cup of dark liquid that smelled like hazelnut
          and black tea, a jar of beige whipped coffee, and a plate of wheat
          biscuits. He placed all three items before his mother as she sat
          beside me at the window.
        </p>
        <p className="chapter__text">
          Rosa dipped a spoon into the jar and dug out a generous dollop for the
          top of my latte. Then she grinned and grabbed a wheat biscuit to stick
          into the whip, and we both watched as the biscuit slowly sunk into the
          foamy waves before I saved it. It was sweet and nutty.
        </p>
        <p className="chapter__text">
          I looked up at Rosa over the rim of my first sip. She had the same
          round face as Antonio, but the smile lines about her eyes spoke of a
          hard life. Still, she seemed as content and sure as any queen in her
          castle. I asked her to tell me about the bakery.
        </p>
        <p className="chapter__text">
          “Well, my husband’s family owned a bakery in the Philippines. We had
          lots of fun then and I wanted the same memories for my son, so the
          first chance we got, we bought that little wagon,” Rosa swung a chubby
          arm around to point at the wagon in the corner. The paint was chipped
          away by time and sun, but what was left proclaimed ‘Rosa&Son’ proudly.
        </p>
        <p className="chapter__text">
          “We would drag it to the new food markets popping up everywhere. It
          took some time, but eventually, we worked our way up.”
        </p>
        {/* lg painting of Rosa & Son Food Cart */}
        <div>
          <img
            className="lgpainting"
            src="./images/RosaSonFoodCart.webp"
            alt="Render of a Filipino-Canadian baked good cart"
            width="1080"
            height="1920"
            loading="lazy"
          />
        </div>
        <p className="chapter__text">
          “So you sold bread within your neighbourhood?” I asked.
        </p>
        <p className="chapter__text">
          “Nah. Our area didn’t have a food market for many years. It took
          forever to just get one of those{" "}
          <ExpandableDefinition
            word="community rain gardens"
            paragraphId="p5"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          . We were one of those streets that got depaved last, you know?” I hid
          my second-hand embarrassment behind a slurp of sweet coffee.
          Low-income housing in the Lakeshore District probably needed all the
          new improvements first, but as expected, received them last.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p5"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “Instead, the city just slapped that{" "}
          <ExpandableDefinition
            word="white paint on the roofs and streets"
            paragraphId="p6"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          and called it a day,” Rosa continued. “It was so uncomfortable
          dragging that cart, filled with bread, down the streets, on the
          streetcars, all the way to the markets. And if we ever got caught in
          the rain or snow, everything was ruined. But we kept at it until we
          made enough to get a van.”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p6"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “Do you still have the van?” I asked, a bit hopeful. I imagined a
          faded cube on wheels with large doors in the back, green grass on its
          heels, with the lake in the background. Would make a very pretty
          photo. But Rosa shook her head.
        </p>
        <p className="chapter__text">
          “I gave it to a friend when I bought this place. Easier to distribute
          orders via drones, although who knows how long that will last,” she
          trailed off and looked up, as if expecting a drone to drop out of the
          sky and clatter onto her roof. “Perhaps I should’ve kept it, eh?
          That’s alright. We can get a new one if necessary.” Rosa looked away
          and I couldn't help but notice her brow crease a bit, as if worried.
        </p>
        <p className="chapter__text">
          “It still makes me laugh thinking about those early days. You might be
          too young, but do you remember those silly leaflets? The ones that
          told people how much emissions their diet was creating?"
        </p>
        <p className="chapter__text">
          My memory supplied a slew of politically motivated videos in the
          archives about money wasted on government-funded initiatives that put
          too much onus on the consumer. I scratched in a note to find them and
          a digital version of the leaflet to attach as supplementary material.
        </p>
        <FoodImpactDiagram />
        <ExpandableDefinition
          word="Food Impact Diagram Source"
          paragraphId="p7"
          onExpand={handleExpand}
          onHover={handleHover}
        />{" "}
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p7"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text"></p>
        <p className="chapter__text">
          “We wouldn’t have been able to afford to buy the eco-swaps the
          leaflets were recommending at our shops,” Rosa continued. “But we
          still helped distribute the information as part of the small business
          programs we were getting grants from.”
        </p>
        <p className="chapter__text">
          "How long did it take for you to feel like you could...er...take part
          in the program as intended?" I asked with a wince.
        </p>
        <p className="chapter__text">
          "Maybe around 2023? That's when we started to make enough money to use
          the eco-swaps, which made us eligible for more grants that we put
          towards a proper kitchen. Probably around the same time, the city
          realized that our neighbourhood probably needed the food markets and
          gardens and{" "}
          <ExpandableDefinition
            word="fancy living pergolas"
            paragraphId="p8"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          more than the people who had time and money to drive anywhere to
          “follow recommendations”.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p8"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          "That's three years into the program..." Rosa nodded at my wonder and
          munched on a biscuit. "That long?"
        </p>
        <p className="chapter__text">
          "It wasn't like how it is now. There was little to no support for
          small or medium businesses and it took us so much effort to get up and
          running. I had some help from family, but I had to hold a couple of
          jobs for a few years."
        </p>
        <p className="chapter__text">
          "So how did you..." I couldn't help but nod in the direction of
          Antonio. I estimated that he couldn’t have been over 30 now, which
          meant he would have been a teenager at the time. Even from here, I
          could see the slim profile of a hearing aid fitted snugly around one
          ear. Rosa also looked at him and smiled softly.
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: lg painting, fill
          <br /> story: painting of Antonio working with a customer, with
          interviewer and Rosa in background against window so backlit
          <br /> feeling: calm security
          <br />
        </span>
        <p className="chapter__text">
          "We resisted getting help at first, like welfare or using food banks
          when we arrived. I know it shouldn't have mattered because hey, I was
          a widow with a kid, stuck in low-income housing and trying to hold two
          jobs at the same time.” Rosa paused and a flicker of insecurity made
          her lips grimace. “Sometimes I felt like a proper statistic.”
        </p>
        <p className="chapter__text">
          “But eventually, I couldn't deny that{" "}
          <ExpandableDefinition
            word="after rent, we had to choose between hydro or food"
            paragraphId="p9"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          ." She paused to visibly shudder as if she’d been thrust into a bad
          dream for just a second.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p9"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          "And the winters here…well I'm told they used to be way worse but no
          one can survive here without heating. So, we came to depend on the
          food banks during the first year. Thankfully that was well before the
          food emergencies were declared, so there was plenty to go around. Or
          seemed like it at the time."
        </p>
        <p className="chapter__text">
          My face must have betrayed me before I could duck it down and mime
          scratching notes. I felt a soft, warm hand cover my own and looked up.
          Rosa said her next words with great care.
        </p>
        <p className="chapter__text">
          "When I think back on those times, I don’t even get sad. I’m just
          impressed we made it.”
        </p>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: sm painting,
          <br /> story: profile photo of Rosa and Antonio, in the bakery with
          the wagon and wall of ingredients in the back
          <br /> feeling: triumph
          <br />
        </span>
      </section>

      {/* section: 2025 - 2029 */}
      <section id="2025-2029" className="story container section">
        <h3 className="chapter__subtitle">Orson</h3>
        <h2 className="chapter__title-year">2025 - 2029</h2>
        <p className="chapter__text">
          An hour after Rosa’s interview, I found myself munching on
          ube-flavoured brioche bun dusted with shredded salty coconut, and
          making marks all over the pages of my notebook. Despite being so old,
          the nifty device recorded the interview in both text and sound fairly
          reliably. When I moved the photos I'd captured around the pages with
          my stylus, the text spread and rippled apart like water making way for
          a floating leaf. I sipped on a glass of iced tea and absently moved
          images around without purpose.
        </p>
        <p className="chapter__text">
          "Excuse me, can I get an apple butter danish and a matcha latte…yeah
          cold, please? Thank you!”{" "}
        </p>

        {/* danish and greentea insert */}
        <div className="canvas-container" ref={ref4}>
          {inView4 && (
            <Suspense fallback={<p>Loading items...</p>}>
              <LazyDanishgreentea />
            </Suspense>
          )}
        </div>

        <p className="chapter__text">
          I heard a voice ring out. Orson Makwa Lee’s voice was clear as a bell,
          just as it had been on our call. The bakery was finally getting busy
          and I watched the tall, thin man chat easily with other customers as
          if he’d grown up with them. Once his order was in hand, I stood up and
          waved until I caught his eye. He raised his danish in recognition and
          snaked his ways through the tables to the window bar.
        </p>
        <p className="chapter__text">
          We made our pleasantries and I blinked a couple of times to take his
          photo. Orson laughed and posed with his pastry up by his ear. The
          apple jam and pats of butter almost threatened to spill over the
          bubbly, crusty edge. Once he’d settled into his seat facing the
          window, he took a proper bite and moaned a bit.
        </p>
        <p className="chapter__text">
          "You know, I connected Rosa to the farmer who made these apples, eh?"
          Orson shared around a mouthful. "Her family grows a bunch of varieties
          and even experiments with new types that can withstand floods. Not the
          floods we get now, but the{" "}
          <ExpandableDefinition
            word="floods we’re predicted to get in 50 years"
            paragraphId="p10"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          with the moderate climate models." He nodded at my impressed look.
          Despite it being cold, Orson blew across the frothy top of his green
          tea and took a sip. He was probably the type who blew on ice cream
          before eating it. "So…you wanted to know more about my work in the
          Sponge?"
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p10"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: smpainting
          <br /> description: drone and person picking apples in a flooded
          orchard
          <br /> feeling:???
        </span>
        <p className="chapter__text">
          “Right. Your enterprise connects farmers and ingredients to customers
          here, like Rosa and the apple farmer, I guess, but you mentioned on
          our call that this wasn’t the first time you worked here.”
        </p>
        <p className="chapter__text">
          “No, I’ve done several jobs in this specific area in the last 15
          years, starting from my first apprenticeship right after high school.
          They had well-paid posts here back in the day.”
        </p>
        <p className="chapter__text">
          “But that’s," I did a quick calculation in my head, "at least a decade
          before the Sponge was fully built, no?”
        </p>
        <p className="chapter__text">
          “Yup. It was mainly a construction site. Nowhere near as beautiful or
          bountiful as it is now.” Orson saluted the deer with his glass as it
          walked across our field of vision. It twisted to scratch itself behind
          its ear in response. Pixels flew off in sparks.
        </p>
        <p className="chapter__text">“So...did you work in construction?”</p>
        <p className="chapter__text">
          “No, I’ve only ever worked in the food industry - mostly delivery and
          networking. I tried to branch out into other things, but always lost
          interest quickly.” Orson shook his head and used the crusty edge of
          the danish to mop up spilled jam. “Nowadays lots of people here work
          in or adjacent to food and nutrition, but it's mainly out of
          necessity; because it touches most industries. But my interest is way
          more ingrained. Guess I'm lucky to have so many options.”
        </p>
        <p className="chapter__text">
          ”And why is that?” My temple twitched erratically and my lens
          awkwardly focused on his face, ready to capture everything. Orson
          chuckled at my intensity and leaned in as if telling me a secret.
        </p>
        <p className="chapter__text">
          “Because I experienced real hunger when the{" "}
          <ExpandableDefinition
            word="Terminal flooded"
            paragraphId="p11"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          .”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p11"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          His words bled boldly across my notebook, and we both flinched in
          embarrassed shock. Some journalist I was, letting my feelings manifest
          so plainly across my page and face.
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: painting + diagram, fill
          <br /> description: scientific diagram of food-insecure neighbourhoods
          that rely on the terminal, with flood risk data, and Orson's words
          going across the page
          <br /> feeling: helplessness
        </span>
        <p className="chapter__text">
          The terminal used to be where most of the whole foods that entered the
          city filtered through. If you didn't live near a large grocery chain,
          all your basic nutrition passed through the terminal. But it had been
          built in a high flood-risk zone, and approvals for flood protections
          came too late. When a string of devastating rainstorms hit WateryTrees
          in June and July of 2024, the subsequent floods knocked out power for
          days and undermined the aging terminal’s infastructure. Vulnerable
          neighbourhoods had been cut off from vital supply lines before
          short-term fixes could be made. Neighbourhoods like Orson's, which
          were considered food insecure by city and industry analysts decades
          before the 100-year plan had started to take shape.
        </p>
        <p className="chapter__text">
          Based on what I knew about him, Orson couldn’t have been out of high
          school at the time.
        </p>
        <p className="chapter__text">
          “That summer really drove home just how vulnerable people like me, my
          family, my neighbours were. Not just in income but in accessible
          resources and robust supply lines.”
        </p>
        <p className="chapter__text">
          “That building was the only place the stores in my area got fresh
          produce from. We didn’t have the micro-farms the suburbs could rely
          on. The{" "}
          <ExpandableDefinition
            word="vertical gardens"
            paragraphId="p12"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          were only just starting...I know they were open to everyone but it's
          hard going into places filled with only dressed-up bougie or business
          folks, you know? Didn't feel like we belonged. Instead, we had to
          travel quite far to the big stores and accepted their price-gouging.
          Sometimes food was trucked in, but we didn't have enough storage...and
          I mean, I'm in the oldest kid, eh? The others had to eat. So
          yeah...there were quite a few days I remember going to school hungry.”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p12"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <span className="placeholder__dataviz">
          Placeholder
          <br />
          <br /> type: dataviz
          <br /> description: who's most likely of all the characters to visit
          food bank
        </span>
        <ExpandableDefinition
          word="dataviz who's more likely to require the food bank"
          paragraphId="p13"
          onExpand={handleExpand}
          onHover={handleHover}
        />
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p13"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          Orson pressed his fingers to the buttery crumbs on his plate to pick
          them up.
        </p>
        <p className="chapter__text">
          “You know, I don’t think I’ve ever talked to anyone about their
          experience with the 2024 flood. I knew the impact was bad, but no one
          seemed keen to talk about it at the time,” I said, a bit lamely. Maybe
          I just didn’t know anyone directly affected by it, my brain supplied.
          Maybe I hadn't bothered to ask, or even wanted to know.
        </p>
        <p className="chapter__text">
          “Well, no one likes complaining when their problems are really real,
          eh?” Orson laughed.
        </p>
        <p className="chapter__text">
          “True…so you and your neighbours experienced the direct impact of
          flooding on your food supply. And this is why you chose to work in the
          Sponge after high school?”
        </p>
        <p className="chapter__text">
          “Nah, that was just...serendipity? Sometime between the flood and us
          finally getting some help with a community glasshouse, a lady showed
          up and set up a kiosk in our dingy lobby. Said the city was
          advertising positions for the new apprentice programs. I wasn't sure
          university was for me, and with all the talk about soft-body AI taking
          over the trades, I figured I'd better try the program.”
        </p>
        <p className="chapter__text">
          “It just so happened that the Ships had arrived in the harbour, and as
          they were focused on getting produce and meats delivered to areas like
          mine, but I never asked to work with them, eh? I just got assigned to
          one of them via a random lottery.” At this, Orson pointed to the
          timber wall on his right.
        </p>
        <p className="chapter__text">
          We couldn’t see them from here, but outside the window, and just west
          of the relatively new wetland, there were three large cargo ships
          anchored permanently in the harbour, undergoing their own
          transformation.
        </p>
        <p className="chapter__text">
          “I know people aren’t too fond of the Ships now but back then, I felt
          really lucky. It was really fun,” he continued. “They rotated us every
          three months, so I got to do a bunch of things. Let’s see, I helped
          program the augmented reality communications.” He started counting off
          tasks on his left hand with a crumb-dusted finger.
        </p>
        <p className="chapter__text">
          “I flew the delivery drones from the ships to the outer neighbourhoods
          previously affected by the flood, including my own. That felt really
          good. Oh! I monitored the community gardens nearer to the harbour that
          wanted to sell produce. We’d go door to door and inspect everything -
          the materials they used to build the gardens, the site maintenance,
          the levels of potassium and nitrogen in the wastewater and soil, and
          food safety practices...”
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: scientific diagram, fill
          <br /> description: delivery drones and city AI bots, compare and
          contrast
        </span>
        <p className="chapter__text">
          “Was that the pilot program for the{" "}
          <ExpandableDefinition
            word="EcoSafe program"
            paragraphId="p14"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          ?” I referenced the new division of the restaurant health inspectors.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p14"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          ”Oh, I suppose it was. Huh. I mean, it makes sense right? No point in
          building all this new infrastructure if we’re going to wreck it with a
          full load of unnecessary chemicals.”
        </p>
        <p className="chapter__text">
          “Agreed. So...the Apprenticeship? That's usually three years max. What
          did you get up to next in the Sponge?”
        </p>
        <p className="chapter__text">
          Orson tapped the side of his head and tossed me a question with raised
          eyebrows. A common expression and pose that often meant, “May I send
          you something?” I nodded and felt the fingers of his other hand, still
          a bit sticky with jam, lightly tap the side of my temple. A slideshow
          only we could see of young kids pulling faces at the camera slipped
          across the window in a grid.
        </p>
        <p className="chapter__text">
          “See these three," He pointed to one photo of his younger self,
          squished in by three other kids. “We started our{" "}
          <ExpandableDefinition
            word="social enterprise"
            paragraphId="p15"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          together. Still working together to this day." I smiled at that and
          pulled a photo of the group closer to me. Their slim arms were pulling
          up chunky robots so that they were also in view. The robots were early
          in their soft-body evolution: simplistic forms armoured with
          segmented, touch-sensitive panels. City-provisioned AI.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p15"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “You worked with a city AI bot?” I asked, roaming my eyes over the
          slip of digital information.
        </p>
        <p className="chapter__text">
          “Yup! They had{" "}
          <ExpandableDefinition
            word="niche-intelligence"
            paragraphId="p16"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          , but were actually the day-to-day teachers for the apprentices, so we
          each got paired up with one with each rotation. It was so cool to
          watch them interact with the environment. I don't know if the city
          ever got funding for higher models that moved better, but honestly,
          I've always preferred these round boys."
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p16"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: sm painting,
          <br /> story: profile photo of Orson, in the bakery, looking out
          window with half eaten stuff
          <br /> feeling: happy wistful
          <br />
        </span>
      </section>

      {/* section: 2030-2034 */}
      <section id="2030-2034" className="story container section">
        <h3 className="chapter__subtitle">Indra</h3>
        <h2 className="chapter__title-year"> 2030 - 2034</h2>
        <p className="chapter__text">
          My mindless decision to snack throughout the day hurt my tummy more
          than my publisher’s wallet, and as I still had some time before my
          third interview, I decided to get some fresh air.
        </p>
        <p className="chapter__text">
          Small pedestrian bridges traversed over{" "}
          <ExpandableDefinition
            word="the Sponge's many rivers and creeks"
            paragraphId="p17"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          . A light breeze had kicked up over the wetlands by early afternoon,
          and dust and dandelion fluff irritated my sinuses. Swans and ducks
          paddled leisurely in small groups while dragonflies zipped through
          clouds of mating midges, which I did my best to avoid. The wide moist,
          green lace was chockful of sleepy, hazy life.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p17"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          Despite the walk to clear my head and indigestion, I couldn't help but
          think back on Rosa's and Orson's stories, and my useless, knee-jerk
          guilt. I grew up in the{" "}
          <ExpandableDefinition
            word="eastern suburbs near the Greenbelt"
            paragraphId="p18"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          , in a low-middle income faily, and attended{" "}
          <ExpandableDefinition
            word="micro urban farms"
            paragraphId="p18"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          all around me as part of my early childhood education. All government
          subsidized. Location had made my younger self, with tiny hands pulling
          up carrots, and sandeled feet carrying me away from slugs, quite
          lucky.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p18"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          My thoughts and I crossed halfway over a major artery via a wide
          wooden bridge and stopped just under the shadow of the Grow Market,
          the giant elevated crosscountry highway that doubled as commercial
          space for local farms. Far above my head and just under the structure,
          little brown sparrows held furious debates. So deafening was their
          discourse that I missed Indra Sivakumar calling out my name.
        </p>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: sm painting, square, potentially moving
          <br /> story: two sparrows under highway talking to each other
          <br /> feeling: cheeky
          <br />
        </span>
        <p className="chapter__text">
          “Hey! I’m down here!” I whipped wildly, searching for the voice, and
          finally found it directly below me, beckoning me to the murky water
          below. I crossed the bridge fully and ambled down the side of the bank
          to greet a carefully balanced Indra, semi-retired Head of Logistics at
          Great Lakes Harbour, in an orange canoe. Behind him was a massive
          Newfoundland dog guarding a picnic basket.
        </p>
        <p className="chapter__text">
          “How did you find me?” I asked my third interviewee. We weren't
          scheduled to talk for a couple of hours.
        </p>
        <p className="chapter__text">
          “Just a coincidence. Here, there’s some patio seating just over there.
          See it? Meet me over there and we can have some late lunch,” Indra
          twisted to pat the basket as best he could and then paddled off
          towards a string of decks with wide umbrellas. I lightly jogged the
          winding path set under the bents of the towering highway, taking
          pictures semi-automatically with hard blinks. In this tranquil
          setting, It was hard to imagine the buzzing market activity several
          meters above my head.
        </p>
        <p className="chapter__text">
          By the time I reached the patio of his choice, Indra was already
          smoothing down a blanket. His canoe was parked and empty and his
          large, chocolate-brown dog was treading happily through the greying
          algae with her webbed toes. I wondered what the ducks milling about
          thought about this intruder, but decided it would be better to observe
          than ask.
        </p>
        <p className="chapter__text">
          “It’s going to be so hard to give her a bath after this,” Indra shook
          his head with a chuckle. He pulled out the following: a three-tier
          silver tiffin packed with dahl, chana, and spinach curries; an
          aluminum-lined container of still-steaming buttery roti; and a large
          thermos of mango lassi which he poured into two stackable teacups. He
          waved his hand impatiently, inviting me to join him. Despite my full
          stomach, the savoury scents made my mouth water and I dug in eagerly
          and with much thanks.
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: lg painting fill
          <br /> story: painting of lunch between two ppl, with dog and ducks
          swimming in midground, and sponge and highway in background
          <br /> feeling: yummy, warm, lazy
          <br />
        </span>
        <p className="chapter__text">
          “I figured if you were going to write about me, I could at least bribe
          you first.” I looked down at the tips of my fingers covered with bits
          of roti and curry. Clever man, Indra.
        </p>
        <p className="chapter__text">
          “Speaking of which,” I laughed and tapped my temple with a clean
          knuckle to record the audio for now, and hoped it would transcribe
          everything properly later. The patch updates on this particular temple
          sticker model weren’t reliable anymore.
        </p>
        <p className="chapter__text">
          “That deer looks so majestic from here, ah?” Indra indicated the
          massive deer that had come back to the lake. It periodically bent its
          head low. “From the Ships all I can see is its giant, furry ass.
          Totally ruins my view. Look at it, pretending to eat algae. Why does
          it do that?”
        </p>
        <p className="chapter__text">
          “Huh. You know, I’m not sure. Maybe it’s part of their...you know,{" "}
          <ExpandableDefinition
            word="biomimicry"
            paragraphId="p19"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          agenda or whatever theorists call it?” Aside from my early stint as a
          mini microfarmer, I'd spent my whole life as a writer, and as such,
          never really worked with AI personnel. Most of the AI personnel I knew
          were effectively unpaid labour. For the brief time they were
          everywhere, AI just melted into the background of my life. But Indra…
          “Do you have a hypothesis?”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p19"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          Indra shook his head and delicately scooped up some yellow dahl with a
          folded piece of roti. “Nah. I’m just glad all that AI robot nonsense
          is gone."
        </p>
        <p className="chapter__text">
          "Really? I thought when your company was working from the Ships, all
          the labour was AI-driven. Weren't you on the panel that encouraged the
          new mandate?"
        </p>
        <p className="chapter__text">
          The Ships had been among the first corporations, driven by market
          sentiment to "do their bit" to suggest flipping a common model of
          "maximizing yield" to "minimizing waste". Overnight, profits had
          fallen, but a circular economy slowly emerged that is still in place
          today.
        </p>
        <p className="chapter__text">
          "Yes, it's true that all our divers and farmers were AI, and yes, I
          voted for the mandate flip. But there just wasn't enough public
          information available for the AI to implement the circular model
          correctly. Maybe if the Ships hadn't been the first to "try it out",
          you know, before all the kinks had been ironed out, the company would
          still be around," Indra sighed. He looked out at the deer and stuck
          his tongue out at it.
        </p>
        <FLCSunburstDiagram />
        <ExpandableDefinition
          word="Food life cycle information availability"
          paragraphId="p20"
          onExpand={handleExpand}
          onHover={handleHover}
        />
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p20"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          "Honestly, The fact that they didn't just up and disappear immediately
          when their mandate switched from "increase yield" to "minimize waste"
          right away always got to me," he complained. " Instead, it took,
          what…almost 10 years?”
        </p>
        <p className="chapter__text">
          “Yeah..2024 to 2033,2034. What do you mean by ’disappear immediately?’
          ”
        </p>
        <p className="chapter__text">
          “Beta, do you know{" "}
          <ExpandableDefinition
            word="how much energy and water that deer likely consumed"
            paragraphId="p21"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          before it completely disconnected from the data centers?” The 'beta'
          made me feel all of eight, instead of nearly 40 and I scrunched my
          nose at him instead of answering. He sipped innocently on his lassi.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p21"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">“No…” I finally answered.</p>
        <p className="chapter__text">
          “Well I’m not doing your work for you, but I’ll say it was too much,”
          he sighed I made a mental note to find out the numbers later. Then I
          shook my head.
        </p>
        <p className="chapter__text">“Not my area,” I muttered to myself.</p>
        <p className="chapter__text">“What was that?”</p>
        <p className="chapter__text">
          “Uh…how long were you with them? The ships, I mean.”
        </p>
        <p className="chapter__text">
          “I think I'm one of the few humans that worked on the ships the entire
          time," Indra mused.
        </p>
        <p className="chapter__text">
          After the 2024 flood and conveniently-timed price hikes at local
          grocery stores had plunged several neighbourhoods into a severe food
          shortage, a company of shipping containers had floated into the
          harbour. The containers were full of food that was taken by drones,
          trains, and trucks to replenish food stocks and banks in the
          hardest-hit areas. And then they’d stayed. Using a fleet of smaller
          ships to traverse the Great Lakes and connecting rivers for local
          ingredients, the Ships amassed a loyal customer base that allowed them
          to expand across the eastern provinces and states.
        </p>
        <p className="chapter__text">
          "My team did everything: food production and safety, delivery drones,
          tested the future permaculture patches...<em>everything</em>,” He
          emphasized with a pointed look.
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: scientific diagram
          <br /> story: different things the ships worked on, particularly the
          permaculture patches and the resulting ecosystem built around them
          <br />
        </span>
        <p className="chapter__text">
          “I spoke with a former Apprentice, eh?" I offered. "This morning. He
          had nothing but good memories of the Ships.” Indra smiled at that and
          refilled my lassi. He took out a hidden spice shaker from his basket
          and sprinkled the top of my teacup with a little extra cardamom
          powder.
        </p>
        <p className="chapter__text">
          “Hm. Maybe I will read your article if it gets published then. It was
          a very good time. Until the{" "}
          <ExpandableDefinition
            word="Grow Market"
            paragraphId="p22"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          opened up in 2030 and suddenly we weren’t the best offer in town. Oh,
          we had great variety and even better prices. But the Market's a social
          place. And, apparently, humans like humans.”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p22"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “Yeah, humans are weird like that,” I said and leaned over to help
          pack up the mostly empty tiffin boxes.
        </p>
        <p className="chapter__text">
          Indra sighed and made another face at the deer; even from far away, it
          looked like it was staring at us. Then it resumed its circuit around
          the lake.
        </p>
        <p className="chapter__text">
          After a moment, I couldn't help but ask: "Are you wondering if it used
          to work for you?"
        </p>
        <p className="chapter__text">
          "Hmmm? Yeah, I do suspect that it used to be a diver, and they mostly
          worked for us. I heard most prefer their old haunts." We watched
          Indra’s dog slink out of the water, and flop onto a sunny patch of
          mossy rock to dry off.
        </p>
        <p className="chapter__text">
          “What was I saying?" Indra shook his head and gave me a blank look.
          The amber-ish haze was getting to us both, along with the impending
          food coma. "Oh right. The impact on our bottom line didn’t look too
          good. My recommendation was to offer on-board shopping days. But the
          executives weren’t too keen on letting customers on board. Or building
          a retail space on land.”
        </p>
        <p className="chapter__text">“And then?”</p>
        <p className="chapter__text">
          “We did our jobs and tried to find alternative solutions of course.
          The executives took our report, reinterpreted the results and my team
          went from 30 humans to two soft-body bots and a few senior scientists
          overnight. And me, of course, so I could do all the firing myself.
          Could hardly look myself in the mirror for a while. Things went
          downhill from there. Kesari? It’s still cold, miraculously.”
        </p>
        <p className="chapter__text">
          “No thanks,” I regretfully turned down the sweet, sunset orange
          rhombuses made with semolina flour and topped with chopped pecans.
          “That was five years ago? Around the time the first AI ‘left’.”
        </p>
        {/* kesari insert */}
        <div className="canvas-container" ref={ref5}>
          {inView5 && (
            <Suspense fallback={<p>Loading dessert</p>}>
              <LazyKesari />
            </Suspense>
          )}
        </div>
        <p className="chapter__text">
          “Yeah. I mean, profits were fine but not as good as expected so our
          stocks took a hit. Then the board got desperate and demanded we expand
          the harbour network. Try to get a ship in every port on both sides of
          the border. But not every city needed a ship and not every port was
          accessible. There was nowhere else to expand to, and with the AI
          leaving, there weren't enough bodies to handle all the work, eh?”
        </p>
        <p className="chapter__text">
          At this, Indra’s dog huffed and got up to circle up onto the deck. She
          whined and put her head in Indra’s lap.
        </p>
        <p className="chapter__text">“Nice doggy,” I said, fondly.</p>
        <p className="chapter__text">
          “Nice try, you mean,” Indra lifted his arm to keep his kesari well out
          of reach. But he pet her with the other hand and fluffed her ears up.
          She smelled of wet dog and dead algae and put a thorough end to our
          late lunch.
        </p>
        <p className="chapter__text">
          “So the company folded soon after?” I asked.
        </p>
        <p className="chapter__text">
          “In 2035, the next year. Took my pension with them. Thankfully, I have
          my savings and investment plan, so I'm ok. I even bought a unit on one
          of the Ships they abandoned last year." Indra pointed towards the
          three docked Ships that looked like long red and black smudges on the
          horizon. The city now owned them and in a fit of experimentation into
          affordable housing alternatives for all income levels, had converted
          the containers and offices on board into rather chic condos and
          amenities.
        </p>
        <p className="chapter__text">
          “It's quite nice. A lot like the office towers on land. You know? The
          ones that were{" "}
          <ExpandableDefinition
            word="repurposed into housing and vertical gardens"
            paragraphId="p23"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          . I even have a little floating perma-patch I can kayak to every day.
          When I don’t feel like cooking, there are a lot of vegetarian options
          here in the Sponge. And of course, my little baby loves being in the
          water. Don’t you, princess.” Indra squeezed his massive Newfoundlander
          who preened under the attention.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p23"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: sm pic
          <br /> story: profile pic of indra and his dog on the patio
          <br />
        </span>
      </section>

      {/* section: 2035-2039 */}
      <section id="2035-2039" className="story container section">
        <h3 className="chapter__subtitle">Janey</h3>
        <h2 className="chapter__title-year"> 2035 - 2039</h2>
        <p className="chapter__text">
          After I left Indra, I decided to go back to the office to upload all
          my data, and chat with my editor. By the time I returned to the
          bakery, the summer sun was an hour from setting and my fourth
          interview was standing at the counter. An impressive bubble of energy
          in between Undergrad and a Master’s program, Janey Kent wore her{" "}
          <ExpandableDefinition
            word="stewardship"
            paragraphId="p24"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          work uniform - a bright tangerine t-shirt, runners, and cargo shorts
          bulging with who-knows-what. Her braids were mostly hidden by a
          fisherman's hat. I sidled up to her.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p24"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “Did you try the elderberry donut?” She asked me by way of greeting
          while staring at what little was left in the glass case. “It's good,
          right? Normally you can't forage berries this early, but some bushes
          flowered early. I think I'll go with a mushroom curry hand pie though.
          Thanks, Antonio.” Janey tapped loudly on the white square embedded in
          the counter to pay and then had a silent conversation with Antonio
          that involved a lot of exaggerated arm swinging on her part.
          Presumably, he agreed to bring her pastry to our table. Then Janey
          swung around to follow me to a free table near the entrance. I ordered
          the same pie and tapped my finger to pay.
        </p>
        {/* mushroom insert */}
        <div className="canvas-container" ref={ref3}>
          {inView3 && (
            <Suspense fallback={<p>Loading handpie...</p>}>
              <LazyMushroomhandpie />
            </Suspense>
          )}
        </div>
        <p className="chapter__text">
          Then she swung back, nearly tripping herself and taking me down with
          her.
        </p>
        <p className="chapter__text">
          “Sorry, sorry! Er, can I also get a teapot of hot water, please? With
          an infuser? And…two cups! Thank you!” She tumbled herself into the
          chair while I took my own and then whispered conspiratorially, “I
          brought some berries and bog labrador I got from up north a week ago.
          I've a bit of a sore throat, eh, and one of{" "}
          <ExpandableDefinition
            word="the Elders"
            paragraphId="p25"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          recommended this particular mix.”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p25"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “Oh really? It smells very healthy,” I whispered back, just as
          serious.
        </p>
        <p className="chapter__text">
          Antonio brought a platter complete with two mushroom curries wrapped
          in flaky, golden pastry, two ceramic mugs, and a small glass teapot
          full of just boiled water. He gestured for the berries and bog
          lavender. Janey and I watched as he carefully poured the dried herbal
          mix into the silver, cylindrical infuser that was secured to the top
          of the pot. The hot water bloomed with blue and red swirls, before
          settling into a dusky purple. He held up one finger and moved it in
          front of me, then Janey, and then left. One cup each, only.
        </p>
        <p className="chapter__text">
          “Bog labrador leaves are a wee bit toxic. But one cup a day is fine,”
          Janey explained as she filled our cups. We both took a sip and audibly
          sighed. I surveyed Janey for a few minutes while she stared off into
          space, with both hands clasped around her mug. She seemed indifferent
          to the attention, or was merely too tired to notice after a long day.
        </p>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: small painting
          <br /> story: top down view of hands, two ppl watching/talking while
          another pours tea mix into the teacup infuser
          <br /> feeling: pleasant waiting
        </span>
        <p className="chapter__text">
          “Looks like you just got off work. So why don't we start from there?”
          I asked gently, coaxing her back to earth.
        </p>
        <p className="chapter__text">
          “Oh right, sorry! The interview. Um, let's see. I joined the
          stewardship program...five years ago? Something like that. When the
          Sponge first officially opened. Just something to do during my gap
          year."
        </p>
        <p className="chapter__text">
          "Did you always work in this part of the Sponge?" I asked. Janey
          nodded.
        </p>
        <p className="chapter__text">
          "Yeah. I really like to cook at home. I'm not very good at it, but I
          like to experiment with new ingredients and techniques. And this
          specific area of the wetlands is dedicated to{" "}
          <ExpandableDefinition
            word="Food Systems Education"
            paragraphId="p26"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          and related pilot programs. That's why we have so many restaurants in
          this area. Plus it’s near the Grow Market, so we can get the vendors
          whatever ingredients we can’t grow here.”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p26"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: lg painting fill
          <br /> story: twilight painting of a stretch of restaurants backed up
          on toa wetland and ppl canoesinga nd getting items
          <br /> feeling: bustle
        </span>
        <p className="chapter__text">
          She took a bite of the mushroom pie and moaned loud enough that even I
          was embarrassed. “This place is my favourite by far though.” Rosa
          happened to be walking by, and she patted Janey good-naturedly on the
          shoulder.
        </p>
        <p className="chapter__text">
          “Yeah, I’m glad you recommended it,” I laughed.
        </p>
        <p className="chapter__text">
          “I’m going to continue studying agro-forestry next year, but decided
          to work here for the season for some more experience. It’s a really
          good post.{" "}
          <ExpandableDefinition
            word="Visitors are actively encouraged to forage here"
            paragraphId="p27"
            onExpand={handleExpand}
            onHover={handleHover}
          />
          , so I’m talking to people all the time. You know, explaining rules,
          best practices, offering recipes. That sort of thing.”
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p27"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “Right. You mentioned a book of plants and recipes?”
        </p>
        <p className="chapter__text">
          “Plants, mushrooms, insects, all sorts of things,” Janey answered
          distractedly, rummaging around in one of the low-hanging pockets of
          her cargo shorts. She pulled out a tiny, chunky book and handed it
          over.
        </p>
        <p className="chapter__text">
          “We made this so people know what's actually edible, when to pick
          them, how to prepare them,” she continued as I flipped through the
          pages. I had to squint to read some of the captions. “Sorry, I know
          it’s a bit difficult to read. Here I can give you the QR code. Are you
          still using AR?”
        </p>
        <p className="chapter__text">
          “Yeah, for as long as it’s not too glitchy to use,” I said. I tapped
          my temple sticker and glared fiercely at the offered QR code to scan
          it. The effort nearly made my eyes ache. I wondered if it would be
          worth upgrading my lenses but more and more services were neglecting
          their augmented reality updates these days. Still, the book download
          was fairly fast for the large amount of content and I started flipping
          through the pages that floated in front of my view of the world.
        </p>
        <p className="chapter__text">
          “Here test it on this.” Janey pulled out a cutting of manoomin trapped
          in a resin block from yet another pocket. A long, drying blade of
          grass tipped with reddish rice, harvested on the lake. When I looked
          at it, the pages shuffled like cards until the right entry was found.
          Information on the plant, its place in the ecosystem, its species
          protection, and how to harvest and use filled in until I could hardly
          see the bakery behind it all.
        </p>
        <span className="placeholder__3dmodel">
          Placeholder
          <br />
          <br /> type: 3d interactive model
          <br /> model: resin block of manoomin with information about it
          <br />
        </span>
        <p className="chapter__text">
          “There’s no way that little book could hold this much information, I
          shook the cards of information away and blinked at Janey who nodded.
        </p>
        <p className="chapter__text">
          “All this info was collected by AI right before the update fully took
          hold and, y'know, they turned into animals and plants and...stuff. The
          digital version is no longer updated as frequently, and what info is
          available is difficult to organize. But you can still call up any
          existing page just by looking at a specimen in the real world.”
        </p>
        <p className="chapter__text">
          “Well, it’s amazing. Yeesh. I’d totally forgotten that ten, fifteen
          years ago, it was normal to have this much information on...well,
          anything,” I exclaimed. Before the 'update', artificial intelligence
          gathered data and followed common Librarian codes of conduct by not
          holding back any information. Without AI, information was slowly
          gathered and ingested. Curated to what was considered important, for
          better or worse.
        </p>
        <p className="chapter__text">
          “Well, I don’t mind the book, but I really hope we don’t totally lose
          AR too because updating a physical book takes forever and is so
          costly. We do have a digital pdf, but carrying around a huge screen is
          such a pain,” she mumbled that last bit as she sipped her cooling tea.
          Then she sat up straight and laughed. “Well, we do what we must, eh?
          The wetlands are too important and people need information to stay
          correctly engaged.”
        </p>
        <p className="chapter__text">
          “So do you and the people at work think the 100-year plan is working
          out? Did restoring the wetlands, making all these changes, make the
          city more food secure?” I asked.
        </p>
        <p className="chapter__text">
          "Hmmm…I agree that the wetlands helped make the city physically
          resistant to floods and gave us a habitat that we really needed. Since
          the municipality doesn’t need to budget so much for disaster recovery,
          they can put more into other services, like the food programs, I
          guess,” I focused my lenses on Janey as she worked through the answer.
        </p>
        <p className="chapter__text">
          “I’m too young to remember what life was like in WateryTrees during
          the crisis, but…I do remember that my mom and dad changed their jobs
          when we were kids, you know, to save more money.” Janey looked up at
          Antonio and Rosa, and then back at me, and then down at her own shirt.
          She plucked up the middle of her shirt and contemplated the
          upside-down white text and graphics against the tangerine background.
        </p>
        <p className="chapter__text">“And now…?” I encouraged. </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: qualitative dv painting
          <br /> story: lakeshore district with interactive cutouts to show flow
          of resources and services; for mobile may need to stack this?
          <br />
        </span>
        <ExpandableDefinition
          word="Dataviz qualitative viz on Sponge resource and services"
          paragraphId="p28"
          onExpand={handleExpand}
          onHover={handleHover}
        />{" "}
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p28"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “And now they can afford to go back to those jobs and live just as
          well as ever," she shrugged. "I guess what I’m saying is, we could
          have a pretty wetland and all this fancy, circular food tech and
          people might not be able to earn enough, or might need to choose
          between rent and dinner. But by creating these new spaces - the
          wetlands, the food gardens, that giant market floating above us, the
          city also created new industries, extra transit, opportunities, and
          connections. All this stuff people were encouraged to take advantage
          of, regardless of their background, education, or work experience. And
          I think the city, and the people, are starting to forget that,” Janey
          stated and gave me a satisfied look.
        </p>
        <p className="chapter__text">
          "You've been thinking a lot about this, eh?" I commented lightly. A
          photo of Janey against a nearly empty bakery bled into being next to
          the notes in my book.
        </p>
        <p className="chapter__text">”…Yeah, I suppose I have been." </p>
        <p className="chapter__text">
          I regarded my interviewee closely and took the last bite of my hand
          pie. Janey craned her brown neck to take a look at the sky through the
          large window; the indigo hues were starting to overpower the pink and
          orange. She grinned wildly.
        </p>
        <p className="chapter__text">
          “Wanna go watch the night parade?" She asked.
        </p>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: small painting, square
          <br /> story: profile pic of janey, in her work clothes, sitting back
          with her tea, smiling openly to camera, book on table, bakery smudged
          in background
          <br />
          feeling: joy from hardwork
        </span>
      </section>

      {/* section: Afterword2039 */}
      <section id="afterword" className="story container section">
        <h3 className="chapter__subtitle">Afterword</h3>
        <h2 className="chapter__title-year">2039</h2>

        {/* trfile insert */}
        <div className="canvas-container" ref={ref6}>
          {inView6 && (
            <Suspense fallback={<p>Loading jars...</p>}>
              <LazyFruitteatrifle />
            </Suspense>
          )}
        </div>

        <p className="chapter__text">
          Janey and I walked out of the bakery with spoons and two large mason
          jars; one filled with passionfruit tea and swelling basil seeds, and
          another with ‘leftover trifle’. At least, that’s what Rosa called it
          when she pulled out the last bits of donuts, cream, and jam that
          hadn’t sold and shoved them into the jar in layers while Janey and I
          helped Antonio sweep up the closing shop.
        </p>
        <p className="chapter__text">
          “You’ll need a snack for the show,” Rosa had said, and I had accepted
          the jars like I hadn’t gained five pounds already that day.
        </p>
        <p className="chapter__text">
          “I’ll bring the jars back tomorrow after work,” Janey called out to
          Rosa on our way out. Rosa and Antonio both waved us away without
          looking up. Nighttime had fallen over the Sponge like a cool blanket
          dotted with warm, yellow light. Visitors slowly wound their way out of
          the Sponge by foot on the bridges or via water taxis strung with fairy
          lights. We followed the mass of moving bodies to the very edge of the
          wetland before Janey pulled me towards one of the large concrete bents
          of the Grow Market.
        </p>
        <p className="chapter__text">
          Rather than helping to keep the elevated highway from falling on our
          heads, this particular bent had been installed with a paternoster
          elevator to move people and products quickly between levels. We ducked
          into and then out of the ever-moving compartment as smoothly as we
          could, albeit with some nervous giggling, and stepped out into the
          market.
        </p>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: small painting, square
          <br /> story: paternoster elevator in the fake bent, bottom floor,
          surrounded by signs and knickknacks that gives off that solarpunk vibe
          we're going for; looks less natural than the
          <br /> feel: anticipation, but exercise caution
        </span>
        <p className="chapter__text">
          The Grow Market was essentially a repurposed highway, eight metres off
          the ground, with the city to the north and the lake to the south. In
          the middle were truck and rail traffic to move goods in, around, and
          out of WateryTrees. A mix of semi-permanent kiosks and permanent
          stores lined the edges of the roads. And on the very outer rim of the
          structure were little bubbling outcrops that ranged from Rain Gardens
          with communal seating to full-on farming operations.
        </p>
        <p className="chapter__text">
          We made our way to one of the south-facing garden outcroppings and
          settled on seating that could have passed for abstract art. For the
          second time that day, I caught my name and I made eye-contact with a a
          tiny, blue-haired woman on the other side of the small, half-sphere
          garden. She sat on a plaid blanket with her friends and a large picnic
          basket. I threw a smile and a tilt of our fruit-tea jar back in
          acknowledgement.
        </p>
        <p className="chapter__text">
          “Friend of yours?” Janey asked. She struggled a bit with the tightly
          sealed steel-lid of the leftover trifle and stuck her spoon in.
        </p>
        <p className="chapter__text">
          “Ah yeah? More like a colleague. She just joined our studio. Think she
          mostly does travel stuff.” I dug my spoon in deeply and pulled out a
          dollop of yeasty, sugar-dusted donut topped with peanut cream and
          raspberry jam.
        </p>
        <p className="chapter__text">“Should we go over and say hi?” </p>
        <p className="chapter__text">
          “Nah.” We settled back into a comfortable silence. The rain garden was
          filling up with more people, talking and laughing amongst their little
          parties. Now and then, I glimpsed a bottle of wine or slips of
          amuse-bouche pass between groups. On the dark water, the deer had
          left, but I felt the anticipation that ‘something’ was going to happen
          as the lights from the busy Market started to dim.
        </p>
        <span className="placeholder__smpainting">
          Placeholder
          <br />
          <br /> type: small painting, square
          <br /> story: super large fish accompanied by smaller fish coming down
          over teh hill and towards the lake
          <br /> feel: wonder
        </span>
        <p className="chapter__text">
          I heard the muffled gasps behind us first. We looked toward the city,
          at the buildings peeking through greenery and the wide road that
          sloped gently downwards towards the sponge. In the middle, emergency
          vehicles and streetcars continued to operate as usual, alongside a
          stream of fish. Likely modeled after{" "}
          <ExpandableDefinition
            word="Pumpkinseed, Walleye, Salmon, Pike"
            paragraphId="p29"
            onExpand={handleExpand}
            onHover={handleHover}
          />{" "}
          , and others, but from up here, they looked more like smudges of
          faintly glowing paint. Some of those smudges were much larger than
          normal and thrashed a bit more than necessary as they passed through
          obstacles in their path.
        </p>
        {/* definition cards */}
        <DefinitionCard
          paragraphId="p29"
          activeCard={displayedCard}
          cardData={cardData}
        />
        <p className="chapter__text">
          “What do you think? Maybe they used to drive the streetcars and
          buses?” I asked Janey. She hummed thoughtfully.
        </p>
        <p className="chapter__text">
          “Maybe? They only come out at night though.” The fish fanned out as
          they reached the wetlands. People from the north side of the Market
          watched the fish migrate under the highway as far as they could before
          giving the thumbs up to those of us on the south side. We abandoned
          our seats to join the growing throng pressed against the railings.
        </p>
        <p className="chapter__text">
          “They never just get into the creeks, they always take the wooden
          bridges to the lake,” I heard a young voice say. Their parents'
          response was muffled, but the observation was true. And for a moment,
          the digital migration looked like the growing roots of a tree before
          it spilled over the edge.
        </p>
        <span className="placeholder__lgpainting">
          Placeholder
          <br />
          <br /> type: lg painting, fill
          <br /> story: digital tree spilling into lake, their blurry forms
          underwater resembling the tree top; camera looking over the backlit
          ppl's shoulders looking south over the wetlands
          <br /> feel: finality
        </span>
      </section>

      {/* section: Footer */}
      <section id="footer" className="story container section">
        <p className="chapter__text">
          ~ Copyright © 2025 Saachi Sadcha - All Rights Reserved. ~
          <br />
          All images, 3D models, and content are original, and created by Saachi
          Sadchatheeswaran. Do not copy, download or sell.
        </p>
        <p className="chapter__text"></p>
      </section>
    </main>
  );
}
